import { observer } from 'mobx-react';
import { memo, useCallback, useEffect, useMemo } from 'react';
import { ItemsInventoryListItem } from '../../../../components/ItemsInventoryListItem/ItemsInventoryListItem';
import { ItemsInventoryListItemSkeleton } from '../../../../components/ItemsInventoryListItemSkeleton/ItemsInventoryListItemSkeleton';
import { IItemsInventoryFilters } from '../../../../types/itemsInventory';
import { itemsInventoryDealStore } from '../../store/itemsInventoryDealStore';
import { Icons } from 'src/assets/fonts/Icons/icons';
import { localeStore } from 'src/mobx/localesStore';
import { formatImagePath } from 'src/utils/formatter';
import { emptyArray } from 'src/utils/helpers';
import Pagination from 'src/view/components/Controls/PaginationOld/PaginationOld';
import { TBooleanInNumber } from '~/types/main';
import { PRODUCTS_SORT_COLUMN } from '~/types/product';
import GridTable, { TGridColumnTitle } from '~/view/containers/GridTable';

export const DealCombosList: React.FC = memo(
  observer((): JSX.Element => {
    const { loading, refreshing, total } = itemsInventoryDealStore.pagination;

    const isNoFound: boolean = !total && !loading;
    const isLoading: boolean = Boolean((loading || refreshing) && !total);

    const columnTitles = useMemo((): TGridColumnTitle<PRODUCTS_SORT_COLUMN>[] => {
      return [
        { title: 'combo_offer', columnName: PRODUCTS_SORT_COLUMN.NAME },
        { title: 'txt_category', columnName: PRODUCTS_SORT_COLUMN.CATEGORY },
        { title: 'order_type' },
        { title: 'txt_price', columnName: PRODUCTS_SORT_COLUMN.PRICE },
        { title: 'sort', columnName: PRODUCTS_SORT_COLUMN.SORT },
        { title: 'in_stock' },
        { title: 'availability' },
      ];
    }, []);

    useEffect(() => {
      itemsInventoryDealStore.fetchItems(1);

      return () => {
        itemsInventoryDealStore.clear();
      };
    }, []);

    const onUpdateFilters = useCallback((filters: Partial<IItemsInventoryFilters>) => {
      itemsInventoryDealStore.setFilters(filters);
    }, []);

    const onChangeStock = useCallback((id: number, stock: TBooleanInNumber) => {
      itemsInventoryDealStore.changeStock(id, stock);
    }, []);

    const onChangeStatus = useCallback((id: number, status: TBooleanInNumber) => {
      itemsInventoryDealStore.changeStatus(id, status);
    }, []);

    return (
      <div className="d-flex flex-column flex-grow-1">
        {isNoFound ? (
          <div className="d-flex flex-column justify-content-center align-items-center mt-6">
            <img src={formatImagePath(`${Icons.Items}.svg`)} alt="no_combo_offers_found" className="image__no-found mb-6" />
            <p className="family-medium text-size-24 color-gray2">{localeStore.t('no_combo_offers_found')}</p>
          </div>
        ) : (
          <GridTable
            gridTemplateClass="items-inventory-list-item__grid-template pr-4"
            columnTitles={columnTitles}
            filters={itemsInventoryDealStore.filters}
            setFilters={onUpdateFilters}
            bodyClassName="gap-20 pr-4"
          >
            {isLoading
              ? emptyArray().map((_, index) => <ItemsInventoryListItemSkeleton key={'skeleton-' + String(index)} />)
              : itemsInventoryDealStore.pagination.data.map((item) => (
                  <ItemsInventoryListItem
                    key={item.id}
                    item={item}
                    onChangeStock={onChangeStock}
                    onChangeStatus={onChangeStatus}
                    branchFilter={itemsInventoryDealStore.filters.branch[0]}
                  />
                ))}
          </GridTable>
        )}

        <Pagination
          type="arrow"
          pagination={itemsInventoryDealStore.pagination}
          onChange={itemsInventoryDealStore.fetchItems}
          showPaginationInfo
          showMorePageButtons
          overlayClassName="overlay_white"
        />
      </div>
    );
  })
);
